import { useMutation, useQuery, useQueryClient } from "react-query";
import { useApiService } from "../useApiService";
import { QUERY } from "../../constants/query";
import { SitePlanControllerService } from "../../services";

export const useUploadSitePlan = () => {
  const { handleRequest } = useApiService();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ propertyId, file }: { propertyId: string; file: File }) => {
      const formData = new FormData();
      formData.append("file", file);

      return handleRequest(
        SitePlanControllerService.sitePlanControllerUploadSitePlanImage(
          { file },
          propertyId
        )
      );
    },
    {
      onSuccess: (response, { propertyId }) => {
        console.log("Site plan uploaded successfully:", response);
        queryClient.invalidateQueries([QUERY.GET_SITE_PLAN, propertyId]);
      },
      onError: (error) => {
        console.error(" Error uploading site plan:", error);
      },
    }
  );
};
export const useGetSitePlan = (propertyId?: string) => {
  const { handleRequest } = useApiService();
  return useQuery(
    [QUERY.GET_SITE_PLAN, propertyId],
    () =>
      handleRequest(
        SitePlanControllerService.sitePlanControllerGetSitePlan(propertyId!)
      ),
    {
      enabled: !!propertyId, // Only fetch if propertyId exists
      retry: false, // Prevents retry loops for 404 errors
      onError: (error) => console.error("Failed to fetch site plan:", error),
    }
  );
};

export const useUpdateSitePlanRotation = () => {
  const { handleRequest } = useApiService();
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      propertyId,
      rotationDegree,
    }: {
      propertyId: string;
      rotationDegree: number;
    }) => {
      return handleRequest(
        SitePlanControllerService.sitePlanControllerUpdateRotation(propertyId, {
          rotationDegree,
        })
      );
    },
    {
      onSuccess: (_, { propertyId }) => {
        console.log(" Rotation degree updated successfully!");
        queryClient.invalidateQueries([QUERY.GET_SITE_PLAN, propertyId]);
      },
      onError: (error) => {
        console.error("Error updating rotation degree:", error);
      },
    }
  );
};

export const useResetSitePlan = () => {
  const queryClient = useQueryClient();
  const { handleRequest } = useApiService();

  return useMutation(
    async ({ propertyId }: { propertyId: string }) => {
      // Make sure the “propertyId” is not empty
      if (!propertyId) {
        throw new Error("PropertyId is required to reset a site plan.");
      }

      // Use your wrapped handleRequest
      return handleRequest(
        SitePlanControllerService.sitePlanControllerResetSitePlan(propertyId)
      );
    },
    {
      onSuccess: (_, { propertyId }) => {
        // Optional: invalidate the GET_SITE_PLAN query so it refetches,
        // which should now show no site plan in DB
        queryClient.invalidateQueries([QUERY.GET_SITE_PLAN, propertyId]);
      },
      onError: (error) => {
        console.error("Error resetting site plan:", error);
      },
    }
  );
};
